/**
 * Spanish for the work order editor, the letter to the crew, the quote
 * check, the reconciliation and the supplier-quote reader (ADR 0325); see
 * index.ts for the house style.
 *
 * What the crew is sent — the email, the text, the printed copy — stays in
 * English; only what the person at the screen reads is here.
 */
export const workOrders: Readonly<Record<string, string>> = {
  // The work order editor
  "Work Order WO-{n}": "Orden de trabajo WO-{n}",
  "Labour for this job. Totals feed the estimate's Labor Cost line.":
    "Mano de obra de este trabajo. Los totales alimentan la línea Costo de mano de obra del presupuesto.",
  Delete: "Eliminar",
  Done: "Listo",
  Dismiss: "Descartar",
  "Delete Work Order WO-{n}?": "¿Eliminar la orden de trabajo WO-{n}?",
  "Its lines go with it. This cannot be undone.":
    "Sus líneas se van con ella. Esto no se puede deshacer.",
  "Google Calendar is out of step with this booking.":
    "Google Calendar no coincide con esta reservación.",
  "Change the day and set it back to try again.":
    "Para intentar de nuevo, cambiar el día y volver a ponerlo.",
  Subtotal: "Subtotal",
  Total: "Total",
  Crew: "Cuadrilla",
  Trades: "Oficios",
  "Ctrl/Cmd-click for more than one.":
    "Ctrl/Cmd + clic para elegir más de uno.",
  "Field Contact": "Contacto en campo",
  Start: "Inicio",
  End: "Fin",
  Status: "Estado",
  Description: "Descripción",
  "Copying…": "Copiando…",
  "Copy From Estimate": "Copiar del presupuesto",
  "The estimate has no lines or scope to copy.":
    "El presupuesto no tiene líneas ni alcance que copiar.",
  "Replace the description with what is on the estimate?":
    "¿Reemplazar la descripción con lo que está en el presupuesto?",
  "What is typed here now will be lost.":
    "Lo que está escrito aquí ahora se perderá.",

  // The status a work order or a material order can hold (orderStatusLabel)
  "Not placed": "Sin colocar",
  Ordered: "Pedido",
  "Picked up": "Recogido",
  "Not scheduled": "Sin programar",
  Scheduled: "Programado",
  Completed: "Completado",
  Paid: "Pagado",

  // The checklist under the order
  "This Order": "Esta orden",
  "Booked in": "Día reservado",
  "Book the day": "Reservar el día",
  "Work complete": "Trabajo completado",
  "Mark complete": "Marcar completado",
  "Crew paid": "Cuadrilla pagada",
  "Mark paid": "Marcar pagado",
  Undo: "Deshacer",
  "Choose the crew that did the work before marking it complete.":
    "Elegir la cuadrilla que hizo el trabajo antes de marcarlo completado.",
  "Work order sent to {to} on {when}.":
    "Orden de trabajo enviada a {to} el {when}.",
  "the crew": "la cuadrilla",
  "The crew has not been sent the work order.":
    "La orden de trabajo no se ha enviado a la cuadrilla.",
  Actions: "Acciones",
  "Send Work Order Again": "Enviar la orden de trabajo otra vez",
  "Send Work Order To Crew": "Enviar orden de trabajo a la cuadrilla",
  "Print For The Crew": "Imprimir para la cuadrilla",
  "Print Without Prices": "Imprimir sin precios",
  'Print opens the print dialog — choose "Save as PDF" to keep a copy. The crew\'s copy carries what they are paid; the other does not.':
    'Imprimir abre el cuadro de impresión — elegir "Guardar como PDF" para conservar una copia. La copia de la cuadrilla lleva lo que se les paga; la otra no.',

  // What reached the crew, said once on screen
  "It did not reach a calendar: {error}":
    "No llegó a ningún calendario: {error}",
  "{who} has no email address, so no invitation was sent.":
    "{who} no tiene email, así que no se envió invitación.",
  " and ": " y ",
  "{crew} has no phone number, so no text was sent.":
    "{crew} no tiene número de teléfono, así que no se envió mensaje.",
  "That crew": "Esa cuadrilla",
  "The crew": "La cuadrilla",
  "{crew} was texted the day.": "Se le envió el día por mensaje a {crew}.",
  "{crew} could not be texted: {error}":
    "No se pudo enviar el mensaje a {crew}: {error}",
  "{crew} was told it is off.": "Se le avisó a {crew} que se canceló.",
  "{crew} could not be told it is off: {error}":
    "No se pudo avisar a {crew} que se canceló: {error}",

  // The payout line under "Crew paid"
  "Payout: {state}, {amount}": "Pago a cuadrilla: {state}, {amount}",
  "waiting for review": "en espera de revisión",
  "approved, to pay": "aprobado, por pagar",
  "paid before review": "pagado antes de la revisión",
  paid: "pagado",
  "sent back": "devuelto",
  "on hold": "en espera",
  billed: "facturado",
  "by {who}": "por {who}",

  // Change orders inside the work order
  "Change Orders": "Órdenes de cambio",
  "1 line the customer agreed to": "1 línea que el cliente aceptó",
  "{n} lines the customer agreed to": "{n} líneas que el cliente aceptó",
  "Extra work the customer agreed to — kept out of the added-after-scheduling figure":
    "Trabajo extra que el cliente aceptó — fuera de la cifra de lo agregado después de programar",

  // The line-item table both order editors share
  Product: "Producto",
  Name: "Nombre",
  Color: "Color",
  Qty: "Cant.",
  Unit: "Unidad",
  Measurement: "Medida",
  "Unit Price": "Precio unitario",
  Amount: "Importe",
  "No line items yet.": "Aún no hay líneas.",
  "+ Add Line Item": "+ Agregar línea",
  "Name this line…": "Nombrar esta línea…",
  "— color —": "— elegir color —",
  entered: "a mano",
  // The units a line can carry (UNITS in packages/core)
  EA: "EA",
  SQ: "SQ",
  SF: "SF",
  BD: "BD",
  RL: "RL",
  BX: "BX",
  PK: "PK",
  LF: "LF",
  GAL: "GAL",
  HR: "HR",
  // What the undo toast says after each edit
  "Typed “{typed}”": "Se escribió “{typed}”",
  "Set {product}": "Se puso {product}",
  "Named “{name}”": "Se nombró “{name}”",
  "Cleared a name": "Se borró un nombre",
  "Set color to {color}": "Color puesto en {color}",
  "Cleared a color": "Se borró un color",
  "Changed a quantity": "Se cambió una cantidad",
  "Changed a unit": "Se cambió una unidad",
  "Changed a price": "Se cambió un precio",

  // Sending the work order to the crew (the letter itself stays English)
  "Send {number} to {crew}": "Enviar {number} a {crew}",
  "Booked for {when}": "Reservado para {when}",
  "No day is booked yet — say so in the message, or book it first.":
    "Aún no hay día reservado — decirlo en el mensaje, o reservarlo primero.",
  "Send work order": "Enviar orden de trabajo",
  "the work order, with what the crew is paid":
    "la orden de trabajo, con lo que se le paga a la cuadrilla",
  "the work order, without prices": "la orden de trabajo, sin precios",
  "{crew} has no email address on file. Add one in Tools → Crews, or type one in below — the day still went to them as a text.":
    "{crew} no tiene email registrado. Agregar uno en Herramientas → Cuadrillas, o escribir uno abajo — el día ya les llegó por mensaje de texto.",
  "This crew": "Esta cuadrilla",
  "Last sent to {to} on {when}.": "Enviada por última vez a {to} el {when}.",
  "What the crew is paid": "Lo que se le paga a la cuadrilla",
  "Put the prices on their copy": "Poner los precios en su copia",

  // Reading a supplier's quote onto a material order
  "Read a supplier's quote": "Leer la cotización de un proveedor",
  Cancel: "Cancelar",
  "Add lines": "Agregar líneas",
  "Add 1 line": "Agregar 1 línea",
  "Add {n} lines": "Agregar {n} líneas",
  "Matched by wording only: {error}": "Emparejado solo por el texto: {error}",
  "Supplier’s quotation": "Cotización del proveedor",
  "A PDF or a photo. The lines are read out for you to check — nothing is added to the order until you press Add.":
    "Un PDF o una foto. Las líneas se leen para revisarlas — nada se agrega a la orden hasta presionar Agregar.",
  "Reading the quote…": "Leyendo la cotización…",
  "Supplier not named on the page": "La página no nombra al proveedor",
  "quoted {date}": "cotizado el {date}",
  "expires {date}": "vence el {date}",
  "These lines do not add up to the quote.":
    "Estas líneas no suman lo que dice la cotización.",
  "The page says {printed} and these lines come to {lines}. A line was probably missed or misread — check against the paper before adding.":
    "La página dice {printed} y estas líneas suman {lines}. Probablemente se omitió o se leyó mal una línea — comparar con el papel antes de agregar.",
  "The reader noted:": "El lector anotó:",
  "On the quote": "En la cotización",
  Each: "C/u",
  "Catalogue product": "Producto del catálogo",
  "Include {line}": "Incluir {line}",
  "Quantity for {line}": "Cantidad de {line}",
  "Price for {line}": "Precio de {line}",
  "Product for {line}": "Producto para {line}",
  "Not a catalogue product": "No es producto del catálogo",
  "no total printed on the page": "la página no imprime un total",
  "page says {total}": "la página dice {total}",

  // The quote check on a material order
  "the roof measurements could not be read: {error}":
    "no se pudieron leer las medidas del techo: {error}",
  "It can still be sent.": "Aun así se puede enviar.",
  "This order could not be checked against the quote: {error}.":
    "No se pudo comparar esta orden con la cotización: {error}.",
  "This order has not finished being checked against the quote.":
    "Esta orden todavía no termina de compararse con la cotización.",
  "As ordered the job makes {ordered} ({orderedMargin}), against {quoted} ({quotedMargin}) as quoted.":
    "Tal como se pidió, el trabajo deja {ordered} ({orderedMargin}), frente a {quoted} ({quotedMargin}) según la cotización.",
  "This order has 1 difference from the quote that nobody has reviewed.":
    "Esta orden tiene 1 diferencia con la cotización que nadie ha revisado.",
  "This order has {n} differences from the quote that nobody has reviewed.":
    "Esta orden tiene {n} diferencias con la cotización que nadie ha revisado.",
  "1 quoted part has changed price since the quote":
    "1 pieza cotizada cambió de precio desde la cotización",
  "{n} quoted parts have changed price since the quote":
    "{n} piezas cotizadas cambiaron de precio desde la cotización",
  more: "más",
  less: "menos",
  "{parts}: the materials now cost {amount} {direction} than quoted, so the estimate may need requoting.":
    "{parts}: los materiales ahora cuestan {amount} {direction} que lo cotizado, así que el presupuesto quizá necesite recotizarse.",
  Quantity: "Cantidad",
  Price: "Precio",
  "On the quote, not ordered": "En la cotización, no pedido",
  "Ordered, not on the quote": "Pedido, no está en la cotización",
  Swapped: "Cambiado",
  "Set to quoted": "Poner lo cotizado",
  "Put quoted back": "Devolver lo cotizado",
  Remove: "Quitar",
  Add: "Agregar",
  "{qty} ({squares} SQ)": "{qty} ({squares} cuadros)",
  "Not checked against the quote: this estimate was built by hand or before 16 Sept 2026, so there is nothing to compare with.":
    "No se comparó con la cotización: este presupuesto se hizo a mano o antes del 16 de sept. de 2026, así que no hay con qué comparar.",
  "Not checked against the quote: the estimate does not record the answers it was generated with.":
    "No se comparó con la cotización: el presupuesto no guarda las respuestas con las que se generó.",
  "Could not check this order against the quote: {error}":
    "No se pudo comparar esta orden con la cotización: {error}",
  "Retry quote check": "Reintentar la comparación",
  "Not checked against a quote: this order is not on an estimate.":
    "No se comparó con una cotización: esta orden no está en un presupuesto.",
  "Checking this order against the quote…":
    "Comparando esta orden con la cotización…",
  'Not checked line by line: this order was generated from "{order}" and the estimate from "{estimate}".':
    'No se comparó línea por línea: esta orden se generó desde "{order}" y el presupuesto desde "{estimate}".',
  "another template": "otra plantilla",
  "a different one": "una distinta",
  "another product": "otro producto",
  "Checked against what the estimate sells: {sold}, not the template's {template}, and {squares} squares sold against {measured} measured.":
    "Comparado con lo que vende el presupuesto: {sold}, no el {template} de la plantilla, y {squares} cuadros vendidos frente a {measured} medidos.",
  "Checked against what the estimate sells: {sold}, not the template's {template}:.":
    "Comparado con lo que vende el presupuesto: {sold}, no el {template} de la plantilla.",
  "Checked against what the estimate sells: {squares} squares sold against {measured} measured.":
    "Comparado con lo que vende el presupuesto: {squares} cuadros vendidos frente a {measured} medidos.",
  "Checked against what the estimate sells:.":
    "Comparado con lo que vende el presupuesto.",
  "{part}: {quoted} quoted, {today} today, on {qty} ({sign}{effect})":
    "{part}: {quoted} cotizado, {today} hoy, por {qty} ({sign}{effect})",
  "Jev matched “{line}” to {part} ({sure} sure).":
    "Jev emparejó “{line}” con {part} ({sure} seguro).",
  "a typed line": "una línea escrita a mano",
  "a quoted part": "una pieza cotizada",
  "Not the same part": "No es la misma pieza",
  "Typed lines were not matched: {error}":
    "Las líneas escritas a mano no se emparejaron: {error}",
  "The review was not saved, so this order still reads as unreviewed: {error}":
    "La revisión no se guardó, así que esta orden sigue como no revisada: {error}",
  "Matches the quote.": "Coincide con la cotización.",
  "Set {part} to the quoted quantity": "{part} puesto en la cantidad cotizada",
  "Set {part} to the quoted price": "{part} puesto en el precio cotizado",
  "Put {part} back in place of {ordered}":
    "{part} devuelto en lugar de {ordered}",
  "the ordered part": "la pieza pedida",
  "Added {part} from the quote": "Se agregó {part} desde la cotización",
  "1 difference from the quote": "1 diferencia con la cotización",
  "{n} differences from the quote": "{n} diferencias con la cotización",
  "Order {ordered} against quoted {quoted} ({net}).":
    "Orden {ordered} frente a {quoted} cotizado ({net}).",
  "Margin {ordered} as ordered, {quoted} as quoted ({profit} of gross profit).":
    "Margen {ordered} como se pidió, {quoted} como se cotizó ({profit} de utilidad bruta).",
  Reviewed: "Revisado",
  "Keep all as ordered": "Conservar todo como se pidió",
  What: "Qué",
  Item: "Artículo",
  Quoted: "Cotizado",
  Impact: "Impacto",
  "ordered,": "pedido,",
  quoted: "cotizado",
  Kept: "Conservado",
  "Today’s price: requote rather than set back.":
    "Precio de hoy: recotizar en vez de devolver.",
  Keep: "Conservar",

  // Reconciling a supplier's paperwork against a material order
  "Reconciliation — MO-{n}": "Conciliación — MO-{n}",
  "{document} checked against our order.":
    "{document} comparado con nuestra orden.",
  "1 line matched.": "1 línea coincidió.",
  "{n} lines matched.": "{n} líneas coincidieron.",
  "Put their prices on the order": "Poner sus precios en la orden",
  "order now at their prices": "la orden ya está a sus precios",
  "Draft email to supplier": "Redactar email al proveedor",
  Close: "Cerrar",
  "Our order": "Nuestra orden",
  "Their paperwork": "Sus papeles",
  "Sales tax — ours {rate}%": "Impuesto — nuestro {rate}%",
  ", theirs {rate}%": ", el suyo {rate}%",
  "Use {rate}%": "Usar {rate}%",
  "order now taxed at {rate}%": "la orden ya lleva {rate}% de impuesto",
  Difference: "Diferencia",
  "Every line matches — quantities and prices agree line for line.":
    "Todas las líneas coinciden — cantidades y precios concuerdan línea por línea.",
  "The tax does not: this order is taxed at {rate}% and {document} charges {theirs}.":
    "El impuesto no: esta orden lleva {rate}% y {document} cobra {theirs}.",
  Ours: "Nuestro",
  Theirs: "Suyo",
  "Not on their paperwork": "No está en sus papeles",
  "We did not order this": "No pedimos esto",
  "Use their price": "Usar su precio",
  "Taking a price changes the catalogue for every estimate built from now on. Every change is logged and can be put back from Tools → Price Changes.":
    "Tomar un precio cambia el catálogo para cada presupuesto que se haga de ahora en adelante. Cada cambio queda registrado y se puede devolver desde Herramientas → Cambios de precio.",
  "Tax this order at {rate}%?": "¿Aplicar {rate}% de impuesto a esta orden?",
  "The order is at {ours}% and {document} charges {rate}%. This changes what this order costs the job.":
    "La orden está al {ours}% y {document} cobra {rate}%. Esto cambia lo que esta orden le cuesta al trabajo.",
  "Put the prices on {document} on this order?":
    "¿Poner los precios de {document} en esta orden?",
  "This changes what this order costs the job. The catalogue is not touched.":
    "Esto cambia lo que esta orden le cuesta al trabajo. El catálogo no se toca.",
  'Update the catalogue cost of "{part}"?':
    '¿Actualizar el costo de catálogo de "{part}"?',
  "Now:  ${price}": "Ahora:  ${price}",
  "New:  ${price}": "Nuevo:  ${price}",
  "This does not just fix this order.": "Esto no corrige solo esta orden.",
  "Are you sure?": "¿Seguro?",
  "Every estimate generated from now on will use the new price. Estimates already written are not touched.":
    "Cada presupuesto que se genere de ahora en adelante usará el precio nuevo. Los presupuestos ya escritos no se tocan.",
  "Undo on this row puts it back. Once this window is closed, changing it again means editing the product in Tools.":
    "Deshacer en esta fila lo devuelve. Una vez cerrada esta ventana, cambiarlo otra vez significa editar el producto en Herramientas.",
};
